import { createSlice } from '@reduxjs/toolkit';
import { getPersistedPlaylistSidebarWidth } from '../utils/djSidebarWidth';

const initialState = {
    playlists: [],
    selectedPlaylistId: null,
    selectedPlaylistName: null, // Name of currently loaded playlist
    loading: false,
    error: null,
    sidebarOpen: false,
    sidebarWidth: getPersistedPlaylistSidebarWidth(),
    showSavePlayerModal: false, // Control save player queue modal
    refreshTrigger: 0, // Increment to trigger playlist refresh

    // Track selection state
    selectedTracks: [], // Array of {file, fileName, result} objects
    lastSelectedIndex: null, // For shift+click range selection
    selectionMode: false, // Whether we're in multi-select mode
};

const playlistSlice = createSlice({
    name: 'playlist',
    initialState,
    reducers: {
        // Playlist management
        setPlaylists(state, action) {
            state.playlists = action.payload;
        },
        addPlaylist(state, action) {
            state.playlists.push(action.payload);
        },
        updatePlaylist(state, action) {
            const index = state.playlists.findIndex(p => p.id === action.payload.id);
            if (index !== -1) {
                state.playlists[index] = { ...state.playlists[index], ...action.payload };
            }
        },
        removePlaylist(state, action) {
            state.playlists = state.playlists.filter(p => p.id !== action.payload);
            if (state.selectedPlaylistId === action.payload) {
                state.selectedPlaylistId = null;
            }
        },
        setSelectedPlaylist(state, action) {
            state.selectedPlaylistId = action.payload;
        },
        setSelectedPlaylistName(state, action) {
            state.selectedPlaylistName = action.payload;
        },

        // Loading states
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },

        // Sidebar
        toggleSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        setSidebarWidth(state, action) {
            state.sidebarWidth = action.payload;
        },
        setShowSavePlayerModal(state, action) {
            state.showSavePlayerModal = action.payload;
        },
        triggerPlaylistRefresh(state) {
            state.refreshTrigger += 1;
        },

        // Track selection
        toggleTrackSelection(state, action) {
            const track = action.payload;
            const index = state.selectedTracks.findIndex(t => t.file === track.file);

            if (index === -1) {
                state.selectedTracks.push(track);
            } else {
                state.selectedTracks.splice(index, 1);
            }
        },

        setSelectedTracks(state, action) {
            state.selectedTracks = action.payload;
        },

        selectAllTracks(state, action) {
            state.selectedTracks = action.payload; // Pass all available tracks
        },

        clearSelection(state) {
            state.selectedTracks = [];
            state.lastSelectedIndex = null;
        },

        selectRange(state, action) {
            const { startIndex, endIndex, allTracks } = action.payload;
            const start = Math.min(startIndex, endIndex);
            const end = Math.max(startIndex, endIndex);

            // Add all tracks in range to selection (without duplicates)
            const rangeTracks = allTracks.slice(start, end + 1);
            const selectedFiles = new Set(state.selectedTracks.map(t => t.file));

            rangeTracks.forEach(track => {
                if (!selectedFiles.has(track.file)) {
                    state.selectedTracks.push(track);
                }
            });
        },

        setLastSelectedIndex(state, action) {
            state.lastSelectedIndex = action.payload;
        },

        setSelectionMode(state, action) {
            state.selectionMode = action.payload;
        },

        // Wipe user-scoped state on logout so the next user doesn't briefly
        // see the previous user's array before their own fetch lands. Mirrors
        // clearLikes / clearFollowedArtists in their respective slices. UI
        // state (sidebarOpen / sidebarWidth) is preserved so the sidebar
        // stays where the user left it.
        clearPlaylists(state) {
            state.playlists = [];
            state.selectedPlaylistId = null;
            state.selectedPlaylistName = null;
            state.error = null;
            state.selectedTracks = [];
            state.lastSelectedIndex = null;
            state.selectionMode = false;
        },
    },
});

export const {
    setPlaylists,
    addPlaylist,
    updatePlaylist,
    removePlaylist,
    setSelectedPlaylist,
    setSelectedPlaylistName,
    setLoading,
    setError,
    toggleSidebar,
    setSidebarOpen,
    setSidebarWidth,
    setShowSavePlayerModal,
    triggerPlaylistRefresh,
    toggleTrackSelection,
    setSelectedTracks,
    selectAllTracks,
    clearSelection,
    selectRange,
    setLastSelectedIndex,
    setSelectionMode,
    clearPlaylists,
} = playlistSlice.actions;

export default playlistSlice.reducer;
